/* eslint-disable jira/naming/disallow-filenames */
import BoardPerformanceTabsQuery from '@jira/admin-pages__performance/src/__generated__/BoardPerformanceTabsQuery.graphql.ts';
import BoardPerformanceTabV2Query from '@jira/admin-pages__performance/src/__generated__/BoardPerformanceTabV2Query.graphql.ts';
import LoadTimesTabQuery from '@jira/admin-pages__performance/src/__generated__/LoadTimesTabQuery.graphql';
import OverviewTabQuery from '@jira/admin-pages__performance/src/__generated__/OverviewTabQuery.graphql';
import PerformanceNetworkViewQuery from '@jira/admin-pages__performance/src/__generated__/PerformanceNetworkViewQuery.graphql';
import metricsOverviewQuery from '@jira/admin-pages__performance/src/__generated__/PerformanceOverviewMainQuery.graphql';
import PerformanceOverviewQuery from '@jira/admin-pages__performance/src/__generated__/PerformanceOverviewQuery.graphql';
import RecommendationPanelContextQuery from '@jira/admin-pages__performance/src/__generated__/RecommendationPanelContextQuery.graphql';
import UserSetupDimensionCardsQuery from '@jira/admin-pages__performance/src/__generated__/UserSetupDimensionCardsQuery.graphql';

import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import type { TenantContext } from '@jira/platform__tenant-context-types/src/tenant-context.tsx';
import type {
	JSResourceReference,
	EntryPointComponent,
	PreloadProps,
	EntryPoint,
} from 'react-relay';
import type { OperationType } from 'relay-runtime';
// eslint-disable-next-line jira/import/file-extension-required
import { createPageEntryPoint } from '@jira/platform__routing-create-route-entry-point';
import { JSResourceForInteraction } from '@atlassian/react-async';
import { fg } from '@atlassian/jira-feature-gating';
import { expVal, expValEquals } from '@atlassian/jira-feature-experiments';
// eslint-disable-next-line jira/restricted/@atlassian+jira-entry-point
import type { AnyEntryPoint } from '@jira/platform__entry-point/src/common/types.tsx';
import type { RouterContext } from '@atlassian/react-resource-router';

export const PerformanceMonitorDashboardEntryPoint: {
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	root: JSResourceReference<EntryPointComponent<any, any, any, any> | null>;
	getPreloadProps: (entryPointParams: EntryPointRouteParams) => {
		entryPoints: {
			___pageLayoutRedirect?:
				| {
						entryPoint: AnyEntryPoint;
						entryPointParams: EntryPointRouteParams;
				  }
				| undefined;
			__pageLayoutRightSidebar?:
				| {
						entryPoint: AnyEntryPoint;
						entryPointParams: {
							fallback: (() => JSX.Element) | undefined;
							context: RouterContext;
							tenantContext: TenantContext;
						};
				  }
				| undefined;
			__pageLayoutHorizontalNavigation?:
				| {
						entryPoint: AnyEntryPoint;
						entryPointParams: {
							fallback: (() => JSX.Element) | undefined;
							context: RouterContext;
							tenantContext: TenantContext;
						};
				  }
				| undefined;
			__pageLayoutPreviewPanel?:
				| {
						entryPoint: AnyEntryPoint;
						entryPointParams: EntryPointRouteParams;
				  }
				| undefined;
			__pageLayoutLeftSidebar?:
				| {
						entryPoint: AnyEntryPoint;
						entryPointParams: {
							entryPoints: {
								[x: string]: {
									entryPoint: AnyEntryPoint;
									entryPointParams: EntryPointRouteParams;
								};
							};
							isAppsExpanded: boolean;
							isFiltersExpanded: boolean;
							isPlansExpanded: boolean;
							isProjectsExpanded: boolean;
							isOperationsExpanded: boolean;
							context: RouterContext;
							tenantContext: TenantContext;
						};
				  }
				| undefined;
			__pageLayoutMainContent: {
				entryPoint: Readonly<{
					getPreloadProps: (
						entryPointParams: EntryPointRouteParams, // oxlint-disable-next-line @typescript-eslint/no-explicit-any
					) => PreloadProps<EntryPointRouteParams, any, any, any>;
					// oxlint-disable-next-line @typescript-eslint/no-explicit-any
					root: JSResourceReference<EntryPointComponent<any, any, any, any> | null>;
				}>;
				entryPointParams: EntryPointRouteParams;
			};
			__pageLayoutTopNavigation: {
				entryPoint: EntryPoint<
					EntryPointComponent<
						Record<string, OperationType>,
						// oxlint-disable-next-line @typescript-eslint/no-explicit-any
						Record<string, EntryPoint<any, any> | undefined>,
						{},
						{} | null
					>,
					{}
				>;
				entryPointParams: EntryPointRouteParams;
			};
		};
	};
	toString: () => string;
} = createPageEntryPoint({
	main: {
		root: JSResourceForInteraction(
			() =>
				import(
					/* webpackChunkName: "async-system-performance-dashboard", jiraSpaEntry: "async-system-performance-dashboard" */ '@jira/spa-apps__admin-pages-performance-dashboard/src/DashboardWithErrorBoundary.tsx'
				),
		),
		getPreloadProps: ({ tenantContext, context }: EntryPointRouteParams) => {
			const now = new Date();
			const endDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
			const startDate = new Date(endDate.getTime() - 7 * 24 * 60 * 60 * 1000);

			// Read dimension from URL query parameter, default to 'network_downlink'
			const dimensionParam = context?.query?.dimension;
			const validDimensions = ['cpus', 'network_downlink', 'http_protocol', 'os', 'browser_name'];
			const dimension =
				typeof dimensionParam === 'string' && validDimensions.includes(dimensionParam)
					? dimensionParam
					: 'network_downlink';

			// oxlint-disable-next-line @typescript-eslint/no-explicit-any
			const queries: Record<string, any> = {};

			if (
				!fg('enable_jira_apm_m2') &&
				!expVal('enable_site_optimizer_tab_creation', 'value', false) &&
				fg('enable_cfo_dashboard')
			) {
				queries.performanceLimitsAndCleanupQuery = {
					parameters: metricsOverviewQuery,
					variables: {
						cloudId: tenantContext.cloudId,
						schemaMigrationEnabled: fg('site_optimiser_schema_migration'),
						projectLevelReportingEnabled: fg('slt1-682-fe-project-level-reporting-so'),
						siteOptimizerAddCfoIslMetricsEnabled: fg(
							'slt1-1341_site_optimizer_add_cfo_isl_metrics',
						),
					},
					options: {
						fetchPolicy: 'network-only',
					},
				};
			}

			if (fg('enable_cfo_dashboard')) {
				queries.boardPerformanceQuery = {
					parameters: BoardPerformanceTabsQuery,
					variables: {
						cloudId: tenantContext.cloudId,
						startDate,
						endDate,
						filters: [
							{
								boardFilter: {
									keyword: '',
									performanceStatus: ['SLOW', 'MODERATE', 'FAST'],
									sortInput: [
										{
											key: 'perfStatus',
											order: 'DESC',
										},
										{
											key: 'views',
											order: 'DESC',
										},
									],
								},
							},
						],
						after: '',
					},
					fetchPolicy: 'store-or-network',
				};

				queries.performanceNetworkQuery = {
					parameters: PerformanceNetworkViewQuery,
					variables: {
						cloudId: tenantContext.cloudId,
						startDate,
						endDate,
						granularity: 'DAY',
						...(expValEquals('enable_cfo_deep_linking_dimension', 'value', true, false) && {
							dimension,
						}),
					},
					fetchPolicy: 'store-or-network',
				};

				queries.performanceOverviewQuery = {
					parameters: PerformanceOverviewQuery,
					variables: {
						cloudId: tenantContext.cloudId,
						startDate,
						endDate,
						granularity: 'DAY',
					},
					fetchPolicy: 'store-or-network',
				};

				// Only load recommendationGroupsQuery if experiment is enabled
				if (expValEquals('enable_cfo_recommendations_m1', 'value', true, false)) {
					queries.recommendationGroupsQuery = {
						parameters: RecommendationPanelContextQuery,
						variables: {
							cloudId: tenantContext.cloudId,
						},
						fetchPolicy: 'store-or-network',
					};
				}
			}

			if (fg('enable_jira_apm_m2')) {
				const loadTimesEnd = Date.now();
				const loadTimesStart = loadTimesEnd - 24 * 60 * 60 * 1000;
				queries.loadTimesQuery = {
					parameters: LoadTimesTabQuery,
					variables: {
						cloudId: tenantContext.cloudId,
						startDate: new Date(loadTimesStart).toISOString(),
						endDate: new Date(loadTimesEnd).toISOString(),
						granularity: 'HOUR',
						sortInput: [{ key: 'timestamp', order: 'ASC' }],
					},
					fetchPolicy: 'store-or-network',
				};

				// Board performance: daily-aligned 7-day range
				// End = yesterday 23:59:59 UTC (today midnight - 1s)
				// Start = today midnight - 7 days
				const boardPerfNow = new Date();
				const boardPerfTodayMidnight = new Date(
					Date.UTC(
						boardPerfNow.getUTCFullYear(),
						boardPerfNow.getUTCMonth(),
						boardPerfNow.getUTCDate(),
					),
				);
				const boardPerfEnd = new Date(boardPerfTodayMidnight.getTime() - 1000);
				const boardPerfStart = new Date(boardPerfTodayMidnight.getTime() - 7 * 24 * 60 * 60 * 1000);

				// User setup: weekly-aligned 4-week range
				// End = previous Sunday 23:59:59 UTC (last Monday midnight - 1s)
				// Start = last Monday midnight - 28 days
				const userSetupDayOfWeek = boardPerfNow.getUTCDay();
				const userSetupDaysToLastMonday = userSetupDayOfWeek === 0 ? 6 : userSetupDayOfWeek - 1;
				const userSetupLastMonday = new Date(
					Date.UTC(
						boardPerfNow.getUTCFullYear(),
						boardPerfNow.getUTCMonth(),
						boardPerfNow.getUTCDate() - userSetupDaysToLastMonday,
					),
				);
				const userSetupEnd = new Date(userSetupLastMonday.getTime() - 1000);
				const userSetupStart = new Date(userSetupLastMonday.getTime() - 28 * 24 * 60 * 60 * 1000);

				queries.overviewTabQuery = {
					parameters: OverviewTabQuery,
					variables: {
						cloudId: tenantContext.cloudId,
						startDate: new Date(loadTimesStart).toISOString(),
						endDate: new Date(loadTimesEnd).toISOString(),
						boardPerfStartDate: boardPerfStart.toISOString(),
						boardPerfEndDate: boardPerfEnd.toISOString(),
						userSetupStartDate: userSetupStart.toISOString(),
						userSetupEndDate: userSetupEnd.toISOString(),
					},
					fetchPolicy: 'store-or-network',
				};

				const cardsEnd = new Date();
				const cardsStart = new Date(cardsEnd.getTime() - 28 * 24 * 60 * 60 * 1000);
				queries.userSetupDimensionCardsQuery = {
					parameters: UserSetupDimensionCardsQuery,
					variables: {
						cloudId: tenantContext.cloudId,
						startDate: cardsStart.toISOString(),
						endDate: cardsEnd.toISOString(),
					},
					fetchPolicy: 'store-or-network',
				};

				queries.boardPerformanceQueryV2 = {
					parameters: BoardPerformanceTabV2Query,
					variables: {
						cloudId: tenantContext.cloudId,
						startDate,
						endDate,
						filters: [
							{
								boardFilter: {
									keyword: '',
									performanceStatus: ['SLOW', 'MODERATE', 'FAST'],
								},
							},
						],
						sortInput: [
							{
								key: 'perfStatus',
								order: 'DESC',
							},
							{
								key: 'views',
								order: 'DESC',
							},
						],
						after: '',
					},
					fetchPolicy: 'store-or-network',
				};
			}
			return {
				queries,
			};
		},
	},
});
